import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FileText, TrendingUp, Cog, Briefcase, ArrowRight, ArrowLeft } from 'lucide-react';

const services = [
  {
    id: 'startup-service',
    title: 'Startup Service',
    icon: FileText,
    color: '#4FC3F7',
    description: 'Every great business starts with an idea. The Startup Service gives you the structure and support to take your first steps with confidence — whether you\'re starting from scratch or buying an existing business, we help you understand its viability so you know exactly what you\'re stepping into.'
  },
  {
    id: 'funding-investment-ready',
    title: 'Funding & Investment Ready Service',
    icon: TrendingUp,
    color: '#29ABE2',
    description: 'Preparing to grow, expand, or plan ahead? We build clear 3-way forecasts, simple financial summaries and supporting papers that explain your numbers in a way banks, investors, and government assessors can understand — and we can join lender or investor discussions to help present your case.'
  },
  {
    id: 'cashflow-business-management',
    title: 'Cash Flow & Business Management',
    icon: Cog,
    color: '#1E88C7',
    description: 'A practical review of your cash flow, working capital and profitability so you can clearly see where your business stands and where improvements are needed. We put structure around how the business is run and help you make better day-to-day decisions as the business grows.'
  },
  {
    id: 'business-mentoring',
    title: 'Business Mentoring',
    icon: Briefcase,
    color: '#0B2545',
    description: 'A trusted sounding board and ongoing support for business owners. Through regular check-ins we help you make sense of your numbers, think through key decisions, and stay on top of cash flow and planning — tackling challenges early and keeping your business moving in the right direction.'
  }
];

const Services = () => {
  const [businessName, setBusinessName] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const name = localStorage.getItem('eybBusinessName');
    if (name) {
      setBusinessName(name);
    }
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-mesh">
      {/* Layered animated background */}
      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none"></div>
      <div className="absolute top-20 right-20 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(91,160,208,0.35), transparent 70%)' }}></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full blur-3xl animate-pulse-slow" style={{ background: 'radial-gradient(circle, rgba(0,51,102,0.22), transparent 70%)', animationDelay: '1.5s' }}></div>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_e62fe2c4-7b06-4359-9dcf-9bd67772605b/artifacts/k7n3nl06_Screenshot%202024-07-25%20103325.png" 
            alt="EYB Consulting" 
            className="h-12 w-auto cursor-pointer smooth-transition hover:opacity-80"
            onClick={() => navigate('/')}
          />
          <Button
            onClick={() => navigate('/journey')}
            variant="ghost"
            className="flex items-center gap-2 smooth-transition"
            style={{ color: '#29ABE2' }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Journey Map
          </Button>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-6 py-16">
        {/* Title section */}
        <div className="text-center mb-20 animate-fadeInUp">
          <h1 className="text-5xl md:text-6xl font-semibold mb-6" style={{ color: '#0B0F14' }}>
            Our Services
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            EYB offers comprehensive business consulting services designed to support you at every stage of your journey.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div
                key={service.id}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
              >
                <div className="group bg-white rounded-2xl p-8 hover-lift stage-card-glow border-2 border-transparent hover:border-sky-300 smooth-transition h-full">
                  {/* Glow effect */}
                  <div 
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 smooth-transition blur-xl"
                    style={{ background: `${service.color}20`, zIndex: -1 }}
                  ></div>

                  {/* Icon */}
                  <div className="mb-6">
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center group-hover:scale-110 smooth-transition"
                      style={{ background: `linear-gradient(135deg, ${service.color}15, ${service.color}30)` }}
                    >
                      <Icon className="w-10 h-10" style={{ color: service.color }} />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-2xl font-bold mb-4" style={{ color: service.color }}>
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {service.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA section */}
        <div className="text-center animate-fadeInUp" style={{ animationDelay: '0.5s', opacity: 0, animationFillMode: 'forwards' }}>
          <div className="bg-gradient-to-br from-sky-50 to-blue-100 rounded-3xl p-12 border-2 border-sky-200">
            <h2 className="text-3xl md:text-4xl font-semibold mb-6" style={{ color: '#0B0F14' }}>
              {businessName ? `Ready to see how EYB can help ${businessName}?` : 'Ready to see how EYB can help your business?'}
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Let's discuss your unique challenges and create a tailored solution for your business.
            </p>
            <Button
              onClick={() => navigate('/contact')}
              className="px-10 py-6 text-base font-bold group tracking-wide"
              style={{ background: '#0B2545', color: '#ffffff', borderRadius: '9999px', boxShadow: '0 8px 24px -8px rgba(11, 37, 69, 0.4)' }}
            >
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 smooth-transition" />
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Services;